import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useNavigate, useParams } from "react-router-dom";
import {
  AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid, ReferenceLine, ResponsiveContainer, Tooltip,
} from "recharts";
import {
  ArrowLeft, Sparkles, Trophy, XCircle, Ban, RefreshCcw, CheckCircle2, AlertTriangle, Calendar, Target, Shield, Play, Share2,
} from "lucide-react";
import { api } from "@/lib/api";
import ChallengeRing from "@/components/ChallengeRing";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

const STATUS_STYLE = {
  active:    { color: "#10b981", bg: "rgba(16,185,129,0.15)",  label: "ACTIVE",    Icon: Play },
  passed:    { color: "#f59e0b", bg: "rgba(245,158,11,0.15)",  label: "PASSED",    Icon: Trophy },
  failed:    { color: "#ef4444", bg: "rgba(239,68,68,0.15)",   label: "FAILED",    Icon: XCircle },
  abandoned: { color: "#6b7280", bg: "rgba(107,114,128,0.15)", label: "ABANDONED", Icon: Ban },
};

const TONE_STYLE = {
  euphoric:  { color: "#f59e0b", bg: "rgba(245,158,11,0.15)",  label: "EUPHORIC" },
  confident: { color: "#10b981", bg: "rgba(16,185,129,0.15)",  label: "CONFIDENT" },
  steady:    { color: "#3b82f6", bg: "rgba(59,130,246,0.15)",  label: "STEADY" },
  anxious:   { color: "#f59e0b", bg: "rgba(245,158,11,0.15)",  label: "ANXIOUS" },
  on_edge:   { color: "#ef4444", bg: "rgba(239,68,68,0.15)",   label: "ON EDGE" },
};

const ALLOWED_EMOJIS = ["🚀", "🦁", "🐺", "🎯", "🔥", "💎", "🌊", "⚡", "🌟", "🎲"];

const money = (v) => {
  const n = Number(v || 0);
  const sign = n >= 0 ? "+" : "";
  return `${sign}$${Math.abs(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};
const pct = (v, d = 2) => `${Number(v || 0).toFixed(d)}%`;
const dtShort = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "2-digit" }) : "—");

const ShareDialog = ({ open, onClose, accountId, user, onChange }) => {
  const [displayName, setDisplayName] = useState("");
  const [emoji, setEmoji] = useState("🎯");
  const [status, setStatus] = useState(null);
  const [publishOn, setPublishOn] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !accountId) return;
    (async () => {
      try {
        const r = await api.get(`/accounts/${accountId}/challenge/leaderboard-status`);
        setStatus(r.data);
        setPublishOn(!!r.data?.published);
        if (r.data?.entry?.display_name) setDisplayName(r.data.entry.display_name);
        else setDisplayName(user?.account_name || "Trader");
        if (r.data?.entry?.avatar_emoji) setEmoji(r.data.entry.avatar_emoji);
      } catch { /* silent */ }
    })();
  }, [open, accountId, user]);

  const isPublished = !!status?.published;

  const save = async () => {
    setSaving(true);
    try {
      if (publishOn) {
        if (!displayName.trim()) { toast.error("Pick a display name"); return; }
        await api.post(`/accounts/${accountId}/challenge/publish`, {
          display_name: displayName.trim(),
          avatar_emoji: emoji,
        });
        toast.success(isPublished ? "Leaderboard entry updated" : "Published to leaderboard");
      } else if (isPublished) {
        await api.post(`/accounts/${accountId}/challenge/unpublish`);
        toast.success("Removed from leaderboard");
      }
      onChange?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md bg-[#0a0e1a] border-[#1f2937] text-[#e5e7eb]" data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl tracking-tight">
            {isPublished ? "Your leaderboard entry" : "Publish to Leaderboard"}
          </DialogTitle>
          <DialogDescription className="text-xs text-[#9ca3af]">
            Show up on the public Challenge Leaderboard — pick an anonymous display name and avatar.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <label className="flex items-center justify-between py-2 px-3 rounded border border-[#1f2937] bg-[#111827]" data-testid="publish-toggle-row">
            <div>
              <div className="text-sm font-medium">Make public on Leaderboard</div>
              <div className="text-[11px] text-[#6b7280] mt-0.5">
                Anonymised — only display name, avatar, firm, progress ring, and days count are shown.
              </div>
            </div>
            <Switch
              checked={publishOn}
              onCheckedChange={setPublishOn}
              data-testid="publish-toggle"
            />
          </label>
          <div className={publishOn ? "" : "opacity-40 pointer-events-none"}>
          <div>
            <label className="vx-label mb-1 block">Display name</label>
            <Input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={40}
              placeholder="Anonymous alias — e.g. SmartMoney99"
              data-testid="share-display-name"
              className="bg-[#111827] border-[#1f2937] text-sm"
            />
          </div>
          <div className="mt-3">
            <label className="vx-label mb-1 block">Avatar</label>
            <div className="grid grid-cols-5 gap-2" data-testid="share-emoji-picker">
              {ALLOWED_EMOJIS.map((e) => (
                <button
                  key={e}
                  onClick={() => setEmoji(e)}
                  data-testid={`share-emoji-${e}`}
                  className={`h-11 rounded border text-2xl leading-none transition-colors ${
                    emoji === e ? "border-[#f59e0b] bg-[#f59e0b]/10" : "border-[#1f2937] hover:border-[#374151]"
                  }`}
                >
                  {e}
                </button>
              ))}
            </div>
          </div>
          </div>
        </div>
        <DialogFooter>
          <button
            onClick={save}
            disabled={saving}
            data-testid="share-save-btn"
            className="h-9 px-5 rounded bg-[#f59e0b] hover:bg-[#d97706] text-black font-semibold text-sm flex items-center gap-1 disabled:opacity-60"
          >
            <Trophy className="w-3.5 h-3.5" />
            {saving ? "Saving…" : (publishOn ? (isPublished ? "Update Entry" : "Publish") : (isPublished ? "Remove from Leaderboard" : "Close"))}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const RuleRow = ({ label, met, target, actual, testId }) => (
  <div
    className="flex items-center justify-between py-2 border-b border-[#1f2937] last:border-b-0"
    data-testid={testId}
  >
    <div className="flex items-center gap-2">
      {met ? (
        <CheckCircle2 className="w-4 h-4 text-[#10b981]" strokeWidth={2.25} />
      ) : (
        <AlertTriangle className="w-4 h-4 text-[#f59e0b]" strokeWidth={2.25} />
      )}
      <div>
        <div className="text-sm">{label}</div>
        <div className="text-[10px] font-mono text-[#6b7280]">Target: {target}</div>
      </div>
    </div>
    <div
      className="font-mono text-sm font-bold"
      style={{ color: met ? "#10b981" : "#f59e0b" }}
    >
      {actual}
    </div>
  </div>
);

const TooltipBox = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded border border-[#1f2937] bg-[#0a0e1a] px-3 py-2 text-xs font-mono">
      <div className="text-[#9ca3af]">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2" style={{ color: p.color }}>
          {p.name}: <span className="font-bold">{typeof p.value === "number" ? p.value.toLocaleString() : p.value}</span>
        </div>
      ))}
    </div>
  );
};

export default function PropChallenge() {
  const { account_id: accountId } = useParams();
  const navigate = useNavigate();
  const [challenge, setChallenge] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [coach, setCoach] = useState(null);
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [coachLoading, setCoachLoading] = useState(false);
  const [abandoning, setAbandoning] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [shareStatus, setShareStatus] = useState(null);

  const loadShareStatus = useCallback(async () => {
    if (!accountId) return;
    try {
      const r = await api.get(`/accounts/${accountId}/challenge/leaderboard-status`);
      setShareStatus(r.data);
    } catch { /* silent */ }
  }, [accountId]);

  const load = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const [c, tl, acct] = await Promise.all([
        api.get(`/accounts/${accountId}/challenge`),
        api.get(`/accounts/${accountId}/challenge/timeline`),
        api.get(`/accounts/${accountId}`),
      ]);
      setChallenge(c.data?.challenge || null);
      setTimeline(tl.data?.points || []);
      setAccount(acct.data || null);
    } catch (e) {
      toast.error("Failed to load challenge");
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadShareStatus(); }, [loadShareStatus]);

  const loadCoach = useCallback(async () => {
    if (!accountId || !challenge) return;
    setCoachLoading(true);
    try {
      const r = await api.get(`/accounts/${accountId}/challenge/coach`);
      setCoach(r.data);
    } catch { /* silent */ } finally {
      setCoachLoading(false);
    }
  }, [accountId, challenge]);

  useEffect(() => { if (challenge) loadCoach(); }, [challenge, loadCoach]);

  const abandon = async () => {
    if (!window.confirm(`Abandon the ${challenge?.firm_name} challenge? Your history stays, but the tracker stops.`)) return;
    setAbandoning(true);
    try {
      await api.post(`/accounts/${accountId}/challenge/abandon`);
      toast.success("Challenge abandoned");
      await load();
    } catch (e) {
      toast.error("Failed to abandon");
    } finally { setAbandoning(false); }
  };

  const equitySeries = useMemo(
    () => timeline.map((p) => ({
      day: dtShort(p.day),
      equity: p.equity,
      dd: p.drawdown_pct,
      pnl_pct: p.pnl_pct,
    })),
    [timeline],
  );

  if (loading) {
    return <div className="text-[#9ca3af] font-mono text-sm">Loading challenge…</div>;
  }
  if (!challenge) {
    return (
      <div className="space-y-4" data-testid="prop-empty">
        <button
          onClick={() => navigate("/accounts")}
          className="flex items-center gap-1 text-xs text-[#9ca3af] hover:text-white"
        >
          <ArrowLeft className="w-3 h-3" /> Back to accounts
        </button>
        <div className="vx-card p-10 text-center">
          <Target className="w-10 h-10 text-[#6b7280] mx-auto mb-3" strokeWidth={1.5} />
          <div className="font-heading text-xl font-bold">No active challenge</div>
          <p className="text-sm text-[#9ca3af] mt-2 max-w-md mx-auto">
            Start a prop firm challenge from the /accounts page — Veerox auto-configures risk limits and tracks your progress.
          </p>
        </div>
      </div>
    );
  }

  const style = STATUS_STYLE[challenge.status] || STATUS_STYLE.active;
  const { Icon } = style;
  const targetMet = challenge.current_pnl_pct >= challenge.profit_target_pct;
  const daysMet = challenge.days_traded >= (challenge.min_trading_days || 0);
  const ddOk = challenge.current_drawdown_pct < challenge.max_total_drawdown_pct;
  const ddHeadroom = Math.max(0, challenge.max_total_drawdown_pct - challenge.current_drawdown_pct);
  const daysNeeded = Math.max(0, (challenge.profit_target_pct || 0) - (challenge.current_pnl_pct || 0));

  return (
    <div className="space-y-5" data-testid="prop-page">
      <button
        onClick={() => navigate("/accounts")}
        data-testid="back-to-accounts"
        className="flex items-center gap-1 text-xs text-[#9ca3af] hover:text-white"
      >
        <ArrowLeft className="w-3 h-3" /> Back to accounts
      </button>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="vx-label">Prop challenge</div>
          <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tighter mt-1">
            {challenge.firm_name} <span className="text-[#6b7280]">·</span>{" "}
            <span className="text-[#9ca3af] text-2xl">{challenge.challenge_type}</span>
          </h1>
          <p className="text-sm text-[#9ca3af] mt-2">
            <b className="text-[#e5e7eb]">{account?.account_name}</b> · Started {dtShort(challenge.start_date)}
            {challenge.deadline_date && ` · Deadline ${dtShort(challenge.deadline_date)}`}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border font-mono text-xs font-bold uppercase tracking-widest"
            style={{ background: style.bg, borderColor: style.color, color: style.color }}
            data-testid="challenge-status-badge"
          >
            <Icon className="w-3.5 h-3.5" /> {style.label}
          </span>
          {challenge.status === "active" && (
            <button
              onClick={() => setShowShare(true)}
              data-testid="share-challenge-btn"
              className="h-8 px-3 text-xs rounded border font-mono uppercase tracking-wider flex items-center gap-1.5"
              style={{
                borderColor: shareStatus?.published ? "#10b981" : "#f59e0b",
                color: shareStatus?.published ? "#10b981" : "#f59e0b",
                background: (shareStatus?.published ? "rgba(16,185,129,0.08)" : "rgba(245,158,11,0.08)"),
              }}
            >
              <Share2 className="w-3 h-3" />
              {shareStatus?.published ? "On Leaderboard" : "Publish"}
            </button>
          )}
          {challenge.status === "active" && (
            <button
              onClick={abandon}
              disabled={abandoning}
              data-testid="abandon-btn"
              className="h-8 px-3 text-xs rounded border border-[#1f2937] hover:border-[#ef4444] hover:bg-[#ef4444]/10 text-[#ef4444] font-mono uppercase tracking-wider"
            >
              {abandoning ? "Abandoning…" : "Abandon"}
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="vx-card p-6 flex flex-col items-center justify-center gap-3" data-testid="progress-panel">
          <ChallengeRing
            progressPct={challenge.current_pnl_pct}
            targetPct={challenge.profit_target_pct}
            drawdownPct={challenge.current_drawdown_pct}
            maxDrawdownPct={challenge.max_total_drawdown_pct}
            daysRemaining={challenge.days_remaining}
            status={challenge.status}
            size={200}
            strokeWidth={14}
          />
          <div className="text-[11px] font-mono text-[#9ca3af] text-center">
            <span className="text-[#e5e7eb] font-bold">{money(challenge.current_pnl_usd)}</span>
            {" of "}
            <span className="text-[#e5e7eb] font-bold">{money(challenge.profit_target_usd)}</span>
            {" target"}
          </div>
        </div>

        <div className="vx-card p-4 space-y-2 xl:col-span-2" data-testid="rules-panel">
          <div className="vx-label mb-2">Challenge rules</div>
          <RuleRow
            label="Profit target"
            met={targetMet}
            target={`+${pct(challenge.profit_target_pct)} (${money(challenge.profit_target_usd)})`}
            actual={pct(challenge.current_pnl_pct)}
            testId="rule-profit-target"
          />
          <RuleRow
            label="Max total drawdown"
            met={ddOk}
            target={`≤ ${pct(challenge.max_total_drawdown_pct)}`}
            actual={pct(challenge.current_drawdown_pct)}
            testId="rule-max-drawdown"
          />
          <RuleRow
            label="Max daily loss"
            met={true}
            target={`≤ ${pct(challenge.max_daily_loss_pct)} of daily equity`}
            actual="within limits"
            testId="rule-daily-loss"
          />
          <RuleRow
            label="Minimum trading days"
            met={daysMet}
            target={`≥ ${challenge.min_trading_days} days`}
            actual={`${challenge.days_traded} traded`}
            testId="rule-min-days"
          />
          {challenge.max_trading_days && (
            <RuleRow
              label="Time limit"
              met={(challenge.days_remaining ?? 0) > 0 || challenge.status !== "active"}
              target={`Within ${challenge.max_trading_days} days`}
              actual={`${challenge.days_since_start} elapsed · ${challenge.days_remaining ?? 0} left`}
              testId="rule-time-limit"
            />
          )}
        </div>
      </div>

      <div className="vx-card p-4" data-testid="timeline-panel">
        <div className="flex items-center justify-between mb-3">
          <div className="vx-label">Daily equity & drawdown timeline</div>
          <div className="text-[10px] font-mono text-[#6b7280]">{timeline.length} data points</div>
        </div>
        <div style={{ width: "100%", height: 300 }}>
          <ResponsiveContainer>
            <AreaChart data={equitySeries} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="ptEq" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#10b981" stopOpacity={0.0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#1f2937" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="day" stroke="#6b7280" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} />
              <YAxis
                stroke="#6b7280"
                tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }}
                tickFormatter={(v) => `$${Math.round(v / 1000)}k`}
                domain={["auto", "auto"]}
              />
              <Tooltip content={<TooltipBox />} />
              <ReferenceLine
                y={challenge.starting_balance + (challenge.profit_target_usd || 0)}
                stroke="#10b981"
                strokeDasharray="4 4"
                label={{ value: "Target", fill: "#10b981", fontSize: 10, position: "insideRight" }}
              />
              <ReferenceLine
                y={challenge.starting_balance * (1 - challenge.max_total_drawdown_pct / 100)}
                stroke="#ef4444"
                strokeDasharray="4 4"
                label={{ value: "Max DD", fill: "#ef4444", fontSize: 10, position: "insideRight" }}
              />
              <Area type="monotone" dataKey="equity" stroke="#10b981" strokeWidth={2} fill="url(#ptEq)" name="Equity" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="vx-card p-4 xl:col-span-2" data-testid="coach-panel">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded flex items-center justify-center bg-[#a78bfa]/15 border border-[#a78bfa]/40">
                <Sparkles className="w-3.5 h-3.5 text-[#a78bfa]" />
              </div>
              <div className="vx-label">AI Coach</div>
              {coach?.tone && TONE_STYLE[coach.tone] && (
                <span
                  className="inline-flex items-center px-2 py-0.5 rounded border font-mono text-[10px] font-bold uppercase tracking-widest"
                  style={{
                    color: TONE_STYLE[coach.tone].color,
                    background: TONE_STYLE[coach.tone].bg,
                    borderColor: `${TONE_STYLE[coach.tone].color}66`,
                  }}
                  data-testid="coach-tone-badge"
                  title={`Coach tone: ${coach.tone}`}
                >
                  {TONE_STYLE[coach.tone].label}
                </span>
              )}
            </div>
            <button
              onClick={loadCoach}
              disabled={coachLoading}
              data-testid="coach-refresh-btn"
              className="w-7 h-7 rounded flex items-center justify-center border border-[#1f2937] hover:border-[#374151] hover:bg-[#1f2937] text-[#9ca3af] disabled:opacity-50"
            >
              <RefreshCcw className={`w-3 h-3 ${coachLoading ? "animate-spin" : ""}`} />
            </button>
          </div>
          {coach ? (
            <div className="text-sm text-[#e5e7eb] leading-relaxed">{coach.coach_text}</div>
          ) : (
            <div className="text-xs font-mono text-[#6b7280]">Loading coach advice…</div>
          )}
          {coach?.is_stub && (
            <div className="text-[10px] text-[#6b7280] font-mono mt-2">rule-based · LLM key not set</div>
          )}
        </div>

        <div className="vx-card p-4 space-y-2" data-testid="headroom-panel">
          <div className="vx-label mb-1">Risk headroom</div>
          <div className="flex items-center justify-between text-xs font-mono">
            <span className="text-[#9ca3af]">Drawdown left</span>
            <span
              className="font-bold text-sm"
              style={{ color: ddHeadroom <= 1.5 ? "#ef4444" : "#10b981" }}
            >
              {pct(ddHeadroom)}
            </span>
          </div>
          <div className="w-full h-1.5 rounded-full bg-[#1f2937] overflow-hidden">
            <div
              className="h-full"
              style={{
                width: `${Math.min(100, (ddHeadroom / challenge.max_total_drawdown_pct) * 100)}%`,
                background: ddHeadroom <= 1.5 ? "#ef4444" : "#10b981",
              }}
            />
          </div>
          <div className="flex items-center justify-between text-xs font-mono mt-3">
            <span className="text-[#9ca3af]">Left to earn</span>
            <span className="font-bold text-sm text-[#3b82f6]">{pct(daysNeeded)}</span>
          </div>
          {challenge.days_remaining !== null && challenge.days_remaining !== undefined && challenge.days_remaining > 0 && daysNeeded > 0 && (
            <div className="text-[10px] font-mono text-[#6b7280]">
              ≈ {(daysNeeded / challenge.days_remaining).toFixed(2)}% / day needed
            </div>
          )}
          {challenge.failed_reason && (
            <div className="mt-3 p-2 rounded border border-[#ef4444]/40 bg-[#ef4444]/10 text-xs">
              <div className="text-[10px] uppercase tracking-widest text-[#ef4444] font-bold mb-1">Failure reason</div>
              <div className="text-[#e5e7eb]">{challenge.failed_reason}</div>
            </div>
          )}
        </div>
      </div>

      <ShareDialog
        open={showShare}
        onClose={() => setShowShare(false)}
        accountId={accountId}
        challenge={challenge}
        user={account}
        onChange={loadShareStatus}
      />
    </div>
  );
}
